'use client'

import {
  ComponentPropsWithoutRef,
  createContext,
  useContext,
  ReactNode,
} from 'react'
import * as AccordionPrimitive from '@radix-ui/react-accordion'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'

import { mtc } from '@/styles'
import { Icon } from '../icon'
import { Badge, BadgeColor } from '../badge'
import {
  accordionItemStyle,
  accordionTriggerStyle,
  accordionContentStyle,
  accordionIconStyle,
} from './styles'

// Context to track variant styling
const AccordionVariantContext = createContext<
  'default' | 'text' | 'header' | 'lineItem'
>('default')

interface AccordionItemData {
  title: ReactNode
  content: ReactNode
  badge?: string
  badgeColor?: BadgeColor
  value?: string
}

interface AccordionRootProps {
  variant?: 'default' | 'text' | 'header' | 'lineItem'
  /** Allows either single or multiple items to be expanded at once. */
  type?: 'single' | 'multiple'
  /** When false, expanded items wont be closed when clicked. */
  collapsible?: boolean
  /** When false, expanded items wont be closed when clicked. */
  defaultValue?: string | string[]
  className?: string
  items?: AccordionItemData[]
  children?: ReactNode
}

const AccordionItem = ({
  className,
  value,
  ...props
}: ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>) => {
  const variant = useContext(AccordionVariantContext)
  return (
    <AccordionPrimitive.Item
      value={value}
      className={mtc(accordionItemStyle({ variant }), className)}
      {...props}
    />
  )
}

const AccordionTrigger = ({
  className,
  children,
  badge,
  badgeColor,
  ...props
}: ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> & {
  badge?: string
  badgeColor?: BadgeColor
}) => {
  const variant = useContext(AccordionVariantContext)
  return (
    <AccordionPrimitive.Header className="flex">
      <AccordionPrimitive.Trigger
        className={mtc(accordionTriggerStyle({ variant }), className)}
        {...props}
      >
        {variant === 'header' && (
          <div className="flex items-center gap-x-2">
            {children}
            {badge && (
              <Badge variant="secondary" color={badgeColor || 'green'}>
                {badge}
              </Badge>
            )}
          </div>
        )}
        {variant !== 'header' && children}
        <Icon
          icon={solid('chevron-down')}
          color="neutral-500"
          className={mtc(accordionIconStyle({ variant }))}
        />
      </AccordionPrimitive.Trigger>
    </AccordionPrimitive.Header>
  )
}

const AccordionContent = ({
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>) => {
  const variant = useContext(AccordionVariantContext)
  return (
    <AccordionPrimitive.Content
      className={mtc(accordionContentStyle({ variant }), className)}
      {...props}
    >
      <div className={mtc('pb-4 pt-0', className)}>{children}</div>
    </AccordionPrimitive.Content>
  )
}

const AccordionRoot = ({
  variant = 'default',
  items,
  className,
  children,
  ...props
}: AccordionRootProps &
  ComponentPropsWithoutRef<typeof AccordionPrimitive.Root>) => (
  <AccordionVariantContext.Provider value={variant}>
    <AccordionPrimitive.Root {...props} className={className}>
      {items
        ? items.map((item, i) => (
            <AccordionItem key={item.value ?? i} value={item.value ?? `${i}`}>
              <AccordionTrigger badge={item.badge} badgeColor={item.badgeColor}>
                {item.title}
              </AccordionTrigger>
              <AccordionContent>{item.content}</AccordionContent>
            </AccordionItem>
          ))
        : children}
    </AccordionPrimitive.Root>
  </AccordionVariantContext.Provider>
)

export const Accordion = Object.assign(AccordionRoot, {
  Item: AccordionItem,
  Trigger: AccordionTrigger,
  Content: AccordionContent,
})
